import {
  PropsWithChildren,
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react'
import { useUser } from './User'
import {
  ADDRESS_FORM_ANSWERS,
  ALL_POSSIBLE_ANSWERS,
  PERSONAL_DATA_FORM_ANSWERS,
} from 'features/FlowPrerequirements/StandalonePrereq/StandalonePrereq.types'
import { RarumUserProfile } from 'core/logic/user/user.types'
import { GalleryType } from 'core/logic/gallery/gallery.types'
import { FormFieldView, FormMode } from 'features/CVM88/Form/Form.types'
import {
  useFieldErrors,
  useFormAnswers,
  useFormState,
} from '@onepercentio/one-ui/components/Form/Form.hook'
import { EscapedTranslation, t, tO } from 'translate/i18n'
import { FormField } from '@onepercentio/one-ui/components/Form/FormField/FormField.types'
import { merge, pick } from 'lodash'
import { DOCUMENT_FIELD, cleanFormObject } from 'utility/fields'
import useAsyncControl from '@onepercentio/one-ui/hooks/useAsyncControl'
import { fromProfile, toV2 } from 'utility/address'
import {
  Prerequireable,
  PrereqStep,
  PrerequirementsConfigs,
} from '@onepercentio/rarum-types/_shared'
import { logDevError, useDevelopmentError } from './ErrorReporting'
import { PENDING_STATUSES } from 'components/offer/OfferAuctionRace/KYCActionWrapper/KYCActionWrapper.logic'
import { ALLOW_FOREIGNS } from './FlowPrerequirements.data'
import { Gallery } from '@onepercentio/rarum-types/galleries'
export type {
  Prerequireable,
  PERSONAL_DATA_FIELDS,
} from '@onepercentio/rarum-types/_shared'
export { PrereqStep } from '@onepercentio/rarum-types/_shared'

export const ADDRESS_FORM_FIELDS = (required: boolean) =>
  [
    { type: 'address', optional: !required, id: 'address' },
  ] as const satisfies FormField[]
export const PERSONAL_DATA_FORM_FIELDS = (isForeign: boolean) =>
  [
    {
      type: 'text',
      id: 'name',
    },
    {
      type: 'date',
      id: 'birthDate',
    },
    {
      type: 'text',
      id: 'email',
    },
    {
      type: 'phone',
      id: 'phone',
      optional: false,
    },
    DOCUMENT_FIELD('document'),
  ] as const satisfies FormField[]

export const REQUIRE_DATA_SHARE_FIELD = (
  gallery: GalleryType,
  isRequired: boolean = true
) =>
  [
    // {
    //   type: 'check',
    //   id: 'confirmedDataSharing',
    //   options: [
    //     {
    //       label: (
    //         <EscapedTranslation
    //           code='flowRequirements.steps.personalData.questions.confirm'
    //           props={{
    //             gallery: gallery['pt-BR'].title,
    //           }}
    //         />
    //       ),
    //       labelStr: t('flowRequirements.steps.personalData.questions.confirm', {
    //         gallery: gallery['pt-BR'].title,
    //       }),
    //       value: 'dataSharing',
    //     },
    //   ],
    //   optional: !isRequired,
    // },
  ] as const satisfies FormField[]

export declare type DeepRequired<T> = T extends Object
  ? Required<{
      [K in keyof T]: DeepRequired<T[K]>
    }>
  : NonNullable<T>

export type PrerequireableShape = DeepRequired<Prerequireable>

export const FlowPrerequirementsContext = createContext<{
  entity?: Prerequireable
}>(null as any)

export default function FlowPrerequirementsProvider({
  requirement,
  children,
}: PropsWithChildren<{
  requirement?: Prerequireable
}>) {
  return (
    <FlowPrerequirementsContext.Provider value={{ entity: requirement }}>
      {children}
    </FlowPrerequirementsContext.Provider>
  )
}

function _useCtx() {
  return useContext(FlowPrerequirementsContext)
}

const STEPS_ORDER = [
  PrereqStep.PERSONAL_DATA,
  PrereqStep.ADDRESS,
  PrereqStep.KYC,
] as PrereqStep[]
export function useSteps() {
  const ctx = _useCtx()
  return useMemo(() => {
    if (!ctx.entity) return undefined
    const configuredRequirements = Object.keys(ctx.entity.requirements ?? {})
    return STEPS_ORDER.filter((step) => configuredRequirements.includes(step))
  }, [ctx.entity])
}

export function usePendingSteps(
  galleryToShareDataWith: GalleryType | undefined
) {
  const { profile } = useUser()
  const ctx = _useCtx()
  const requiredSteps = useSteps()
  const logDevError = useDevelopmentError()
  return useMemo(() => {
    const galleryConsents =
      galleryToShareDataWith === undefined
        ? true
        : profile?.personalData?.tenants?.[process.env.REACT_APP_TENANT]
            .galleries?.[galleryToShareDataWith.id]?.dataConsent
    return requiredSteps!.filter((step) => {
      switch (step) {
        case PrereqStep.ADDRESS:
          const addressAnswer = answersForStep(
            step,
            profile!
          ) as ADDRESS_FORM_ANSWERS

          const filledAddress = addressAnswer.address?.[0]

          /** Shows address if some of the address fields are not defined */
          return (
            !galleryConsents ||
            filledAddress === undefined ||
            Object.keys(filledAddress).some(
              (field) =>
                filledAddress[field as keyof typeof filledAddress] === undefined
            )
          )
        case PrereqStep.PERSONAL_DATA:
          const filledPersonalData = answersForStep(step, profile!)
          const requiredFields =
            ctx.entity!.requirements![PrereqStep.PERSONAL_DATA]!
          const isMissingField = requiredFields.some((field) => {
            const answer =
              filledPersonalData![field as keyof typeof filledPersonalData]
            return answer === undefined || answer === null
          })

          /** Show the personal data if:
           * - the user has not accepted data share yet or;
           * - there is a required field that has not been filled yet */
          return !galleryConsents || isMissingField
        case PrereqStep.KYC:
          const kycMethod = ctx.entity!.requirements![
            PrereqStep.KYC
          ] as PrerequirementsConfigs['kyc']
          const kyc =
            profile!.kyc?.[
              kycMethod.toUpperCase() as Uppercase<typeof kycMethod>
            ]

          return [...PENDING_STATUSES, undefined].includes(kyc?.status)
        default:
          logDevError(`There is no condition set for ${step}`, module)
      }
    })
  }, [requiredSteps])
}

export function useStepConfig(step: PrereqStep) {
  const ctx = _useCtx()

  return ctx.entity!.requirements![step]
}

export function useAnswersForStep(step: PrereqStep) {
  const { profile } = useUser()
  return useMemo(() => answersForStep(step, profile!, ''), [step])
}

function answersForStep(
  step: PrereqStep,
  profile: RarumUserProfile,
  defaultAnswer: string | undefined = undefined
) {
  switch (step) {
    case PrereqStep.ADDRESS:
      return {
        address: [toV2(fromProfile(profile)), true, false],
      } as NonNullable<ADDRESS_FORM_ANSWERS>
    case PrereqStep.PERSONAL_DATA:
      return {
        name: profile.personalData?.name ?? defaultAnswer,
        email: profile.personalData?.email ?? defaultAnswer,
        phone: profile.personalData?.phoneNumber
          ? [profile.personalData?.phoneNumber, true, null]
          : undefined,
        document: profile.personalData?.cpf ?? defaultAnswer,
        birthDate: profile.personalData?.birthDate ?? defaultAnswer,
      } satisfies Omit<PERSONAL_DATA_FORM_ANSWERS, 'confirmedDataSharing'>
  }
}

export function useFormForStep(
  prefilledAnswers: ALL_POSSIBLE_ANSWERS,
  step: PrereqStep,
  shouldIncludeDataConsent: boolean,
  galleryToShareDataWith: {
    gallery: Gallery
    isRequired: boolean
  }
) {
  const { profile } = useUser()
  const config = useStepConfig(step)
  const _defaultAnswers = useAnswersForStep(step)
  const defaultAnswers = useMemo(() => {
    if (shouldIncludeDataConsent) {
      const consentsUserHaveGiven =
        profile?.personalData?.tenants?.[process.env.REACT_APP_TENANT]
          ?.galleries?.[galleryToShareDataWith.gallery.id]?.dataConsent?.[0]

      ;(_defaultAnswers as ALL_POSSIBLE_ANSWERS).confirmedDataSharing = [
        consentsUserHaveGiven === undefined
          ? false
          : consentsUserHaveGiven.value,
      ]
    }
    return _defaultAnswers
  }, [shouldIncludeDataConsent])
  const answersState = useFormAnswers<ALL_POSSIBLE_ANSWERS>(
    { ...defaultAnswers, ...prefilledAnswers },
    FormMode.WRITE
  )
  const stepQuestions = useMemo(() => {
    switch (step) {
      case PrereqStep.ADDRESS:
        const isRequired =
          config as PrerequireableShape['requirements']['address']
        return ADDRESS_FORM_FIELDS(isRequired).map((a) => a) as FormFieldView[]
      case PrereqStep.PERSONAL_DATA:
        const tX = (a: keyof ALL_POSSIBLE_ANSWERS) => {
          switch (a) {
            case 'document':
              if (!!answersState.answers.isForeign?.[0])
                return tO(
                  `flowRequirements.steps.personalData.questions.documentForeign`
                )
              else
                return tO(
                  `flowRequirements.steps.personalData.questions.document`
                )
            default:
              return tO(
                `flowRequirements.steps.personalData.questions.${a as 'name'}`
              )
          }
        }

        const isFieldOptional = (
          field: PrerequireableShape['requirements']['personalData'][number]
        ) => {
          const requiredFields =
            config as PrerequireableShape['requirements']['personalData']
          const isFieldRequired = requiredFields?.includes(field) || false

          return isFieldRequired === false ? true : undefined
        }
        return [
          ...PERSONAL_DATA_FORM_FIELDS(!!answersState.answers.isForeign?.[0])
            .filter(Boolean)
            .map((a) => ({
              ...a,
              title: tX(a.id),
              optional: isFieldOptional(a.id as 'document'),
            })),
        ] as FormFieldView[]
      case 'kyc':
        return []
      default:
        logDevError(`The step ${step} is not mapped to have questions`, module)
        return []
    }
  }, [
    step,
    galleryToShareDataWith.gallery,
    answersState.answers.isForeign?.[0],
  ])!

  const [showErrors, setShowErrors] = useState(false)
  useEffect(() => setShowErrors(false), [step])

  const questions = useMemo(() => {
    if (shouldIncludeDataConsent)
      return [
        ...stepQuestions,
        ...REQUIRE_DATA_SHARE_FIELD(
          galleryToShareDataWith.gallery,
          galleryToShareDataWith.isRequired
        ),
      ]
    else return stepQuestions
  }, [stepQuestions])
  const { isQuestionsAnswered, answers, onAnswerAction } = useFormState(
    questions as any,
    answersState as any
  )
  const errors = useFieldErrors(questions, answers, showErrors)

  const sections = useMemo(
    () => [
      {
        title: tO(`flowRequirements.steps.${step}.title`),
        questions,
      },
    ],
    [questions]
  )

  return {
    sections,
    errors,
    isQuestionsAnswered,
    onAnswerAction,
    setShowErrors,
    questions,
    answers,
  }
}

export function usePrerequirements(...prereqs: Prerequireable[]) {
  return useMemo(() => {
    const result = { requirements: {} } as Prerequireable
    for (let prereq of prereqs) merge(result, pick(prereq, 'requirements'))

    return result
  }, [...prereqs])
}

export function useUpdatePrereqAnswers(gallery: GalleryType) {
  const { setAcceptDataUsage, updateProfile, detail } = useUser()

  const savePrereq = useAsyncControl({
    setAcceptDataUsage: async (accept: boolean) =>
      await setAcceptDataUsage(accept, gallery),
    updateProfile: async (
      stepsToUpdate: PrereqStep[],
      answers: ALL_POSSIBLE_ANSWERS
    ) => {
      try {
        if (
          stepsToUpdate.includes(PrereqStep.ADDRESS) &&
          answers.address?.[1]
        ) {
          const address = answers.address[0]
          cleanFormObject(address)
          await updateProfile({
            personalData: {
              ...address,
            },
          })
        }
        if (stepsToUpdate.includes(PrereqStep.PERSONAL_DATA)) {
          const personalDataAnswers = answers as PERSONAL_DATA_FORM_ANSWERS
          const fieldsToUpdate: Partial<RarumUserProfile['personalData']> = {
            name: personalDataAnswers.name ?? '',
            birthDate: personalDataAnswers.birthDate ?? '',
            email: personalDataAnswers.email ?? '',
            cpf: personalDataAnswers.document ?? '',
            phoneNumber: personalDataAnswers.phone?.[0] ?? '',
          }
          fieldsToUpdate.phoneNumber = fieldsToUpdate.phoneNumber!.replace(
            /[^0-9]/g,
            ''
          )
          cleanFormObject(fieldsToUpdate)
          await updateProfile({
            personalData: fieldsToUpdate,
          })
        }
      } catch (e) {
        logDevError(
          `Updating profile failed for user ${detail?.uid} with error ${e.message}`,
          module
        )
      }
    },
  })

  return savePrereq
}
